import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { FileDown, ScanLine, Truck } from "lucide-react";
import { api, apiError, API } from "@/lib/api";
import { useProject } from "@/pages/project/ProjectLayout";
import { useProjectSocket } from "@/hooks/useProjectSocket";
import { ArrivalCalendar, ShipmentSummary } from "@/components/overview/ShipmentSummary";
import { PalletManager } from "@/components/overview/PalletManager";
import { PalletDetailDialog } from "@/components/overview/PalletDetailDialog";
import { QrScannerDialog } from "@/components/overview/QrScannerDialog";
import { OverdueShipmentArchive } from "@/components/overview/OverdueShipmentArchive";
import { useAuth } from "@/context/AuthContext";

/** Accepts a full pallet URL from a QR label or a bare pallet code. */
function palletCodeFrom(text) {
  const raw = (text || "").trim();
  if (!raw) return null;
  try {
    // A pallet label always encodes a URL carrying ?pallet=CODE.
    return new URL(raw).searchParams.get("pallet")?.toUpperCase() || null;
  } catch (error) {
    console.debug("QR payload is not a URL, treating it as a bare pallet code", error);
  }
  return /^[A-Za-z0-9._-]{1,40}$/.test(raw) ? raw.toUpperCase() : null;
}

export default function LogisticsPage() {
  const { projectId, project } = useProject();
  const { capabilities } = useAuth();
  const guest = Boolean(capabilities?.is_guest);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [pallets, setPallets] = useState([]);
  const [overdue, setOverdue] = useState(null);
  const [tab, setTab] = useState(() => (searchParams.get("tab") === "archive" ? "archive" : "active"));
  const [scanOpen, setScanOpen] = useState(false);
  const [palletCode, setPalletCode] = useState(() => searchParams.get("pallet"));
  // Roles are project-scoped; the project payload carries the effective roles.
  const roles = project?.roles || [];
  const canManagePallets =
    !capabilities?.is_guest &&
    (capabilities?.is_admin ||
    (capabilities?.data_editors?.pallet || []).some((r) => roles.includes(r)));

  const load = useCallback(async () => {
    try {
      const [res, palletRes, overdueRes] = await Promise.all([
        api.get(`/projects/${projectId}/overview`),
        api.get(`/projects/${projectId}/pallets`),
        api.get(`/projects/${projectId}/logistics/overdue-shipments`),
      ]);
      setData(res.data);
      setPallets(palletRes.data);
      setOverdue(overdueRes.data);
    } catch (error) {
      toast.error(apiError(error));
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useProjectSocket(projectId, (message) => {
    if (["panel_status_changed", "panel_shipping_changed", "pallet_assigned", "pallet_renamed", "pallet_deleted", "shipment_updated", "shipment_deleted"].includes(message.event)) load();
  });

  if (!data)
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-cyan-400 border-t-transparent" />
      </div>
    );

  const shipped = data.departments.find((d) => d.key === "logistics");
  const counts = Object.fromEntries((shipped?.buckets || []).map((b) => [b.status, b.count]));
  const delivered = data.shipments.reduce((sum, s) => sum + s.delivered, 0);
  const inTransit = data.shipments.reduce((sum, s) => sum + s.in_transit, 0);
  const canConfirmArrival =
    !capabilities?.is_guest && (capabilities?.is_admin || roles.includes("logistics"));

  return (
    <main
      className="okn-scroll mx-auto w-full max-w-[1500px] flex-1 overflow-y-auto px-4 py-6 sm:px-6"
      data-testid="logistics-page"
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-full border border-slate-200 bg-white p-0.5">
          {[
            ["active", "Active"],
            ["archive", `Delayed archive${overdue?.total_panels ? ` (${overdue.total_panels})` : ""}`],
          ].map(([key, label]) => (
            <button
              key={key}
              data-testid={`logistics-tab-${key}`}
              onClick={() => setTab(key)}
              className={`mono rounded-full px-3.5 py-1.5 text-[10.5px] uppercase tracking-wider transition-colors ${
                tab === key
                  ? "bg-cyan-500/10 text-cyan-700"
                  : "text-slate-500 hover:text-slate-800"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-2">
        <button
          data-testid="open-qr-scanner-button"
          onClick={() => setScanOpen(true)}
          className="mono flex items-center gap-2 rounded-full border border-cyan-500/40 bg-white px-4 py-2 text-[10.5px] uppercase tracking-wider text-cyan-700 hover:bg-cyan-50"
        >
          <span className="okn-scan" data-testid="qr-scan-animation">
            <ScanLine size={13} />
          </span>{" "}
          Scan QR
        </button>
        {guest ? null : (
          <a
            data-testid="export-logistics-pdf"
            href={`${API}/projects/${projectId}/exports/logistics.pdf`}
            className="mono flex items-center gap-2 rounded-full border border-slate-200 bg-white px-4 py-2 text-[10.5px] uppercase tracking-wider text-slate-700 hover:border-cyan-400 hover:text-cyan-700"
          >
            <FileDown size={13} /> Export PDF
          </a>
        )}
        </div>
      </div>

      {tab === "archive" ? (
        <OverdueShipmentArchive
          projectId={projectId}
          data={overdue}
          onChanged={load}
          canConfirm={canConfirmArrival}
        />
      ) : (
      <>
      <div className="grid grid-cols-3 gap-2">
        {[
          ["Awaiting shipment", (counts["—"] || 0) + (counts["Not Shipped"] || 0), "slate"],
          ["In transit", inTransit, "cyan"],
          ["Arrived", delivered, "emerald"],
        ].map(([label, value, tone]) => (
          <div
            key={label}
            data-testid={`logistics-metric-${label.replace(/\s+/g, "-").toLowerCase()}`}
            className="okn-rise flex items-center gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
          >
            <div
              className={`grid h-8 w-8 shrink-0 place-items-center rounded-md ${
                tone === "cyan"
                  ? "bg-cyan-500/10 text-cyan-700"
                  : tone === "emerald"
                    ? "bg-emerald-500/10 text-emerald-700"
                    : "bg-slate-100 text-slate-700"
              }`}
            >
              {label === "In transit" && value > 0 ? (
                <span className="okn-truck-stage grid h-full w-full place-items-center">
                  <span className="okn-truck-drive" data-testid="logistics-truck-animation">
                    <span className="okn-truck-bounce">
                      <Truck size={15} />
                    </span>
                  </span>
                  <span className="okn-truck-road" aria-hidden="true" />
                </span>
              ) : label === "Awaiting shipment" && value > 0 ? (
                <span className="okn-breathe" data-testid="logistics-awaiting-animation">
                  <Truck size={15} />
                </span>
              ) : (
                <Truck size={15} />
              )}
            </div>
            <div className="flex min-w-0 items-baseline gap-2 max-sm:flex-col max-sm:items-start max-sm:gap-0.5">
              <p className="mono text-xl font-medium leading-none tracking-tight">{value}</p>
              <p className="text-xs uppercase leading-tight tracking-wider text-slate-500 sm:truncate max-sm:text-[9.5px]">
                {label}
              </p>
            </div>
          </div>
        ))}
      </div>

      {data.shipments.length === 0 ? (
        <p className="mt-4 rounded-xl border border-slate-200 bg-white px-5 py-4 text-sm text-slate-500">
          No shipments yet. Set a panel's Logistics status to <b>Shipped</b> and pick a shipping
          method to start tracking arrivals.
        </p>
      ) : null}

      <ShipmentSummary
        shipments={data.shipments}
        projectId={projectId}
        pallets={pallets}
        canManage={canManagePallets}
        highlight={searchParams.get("highlight")}
        onChanged={load}
      />
      {canManagePallets ? (
        <PalletManager
          projectId={projectId}
          pallets={pallets}
          canManage
          onChanged={load}
        />
      ) : null}
      <div className="pb-4">
        <ArrivalCalendar
          calendar={data.arrival_calendar}
          projectId={projectId}
          onChanged={load}
          canConfirm={canConfirmArrival}
        />
      </div>
      </>
      )}

      <QrScannerDialog
        open={scanOpen}
        onOpenChange={setScanOpen}
        onResult={(text) => {
          const raw = (text || "").trim();
          try {
            // A block-definition label opens the Panel Properties glass view instead.
            const parsed = new URL(raw);
            const short = /^\/q\/[^/]+\/(.+)$/i.exec(parsed.pathname);
            const block = short
              ? decodeURIComponent(short[1])
              : parsed.searchParams.get("block");
            if (block) {
              navigate(`/projects/${projectId}/properties?block=${encodeURIComponent(block)}`);
              return true;
            }
          } catch (error) {
            /* not a URL — fall through to the pallet parser */
            console.debug("[qr] not a URL payload", error);
          }
          const code = palletCodeFrom(text);
          if (!code) {
            toast.error("That QR code is not a pallet label");
            return false;
          }
          setPalletCode(code);
          setSearchParams({ pallet: code });
          return true;
        }}
      />
      <PalletDetailDialog
        projectId={projectId}
        palletCode={palletCode}
        open={!!palletCode}
        canConfirm={canConfirmArrival}
        onOpenChange={(next) => {
          if (!next) {
            setPalletCode(null);
            setSearchParams({});
          }
        }}
        onDone={load}
      />
    </main>
  );
}
